import React from 'react';
import PropTypes from 'prop-types';
import NewsItem from './NewsItem';

/**
 * Renders a grid of news article cards with loading skeleton and empty states.
 * Articles are expected to be pre-normalized by the parent hook.
 *
 * @param {{ articles: Array, loading: boolean, isBookmarked: Function, onToggleBookmark: Function }} props
 */
const NewsList = ({ articles, loading, isBookmarked, onToggleBookmark }) => {
  /** Initial loading state: show skeleton grid. */
  if (loading && articles.length === 0) {
    return (
      <div className="row g-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="col-12 col-sm-6 col-md-4" key={`skeleton-${i}`}>
            <div className="card qs-card" aria-hidden="true">
              <div className="qs-skeleton-img" />
              <div className="card-body">
                <div className="qs-skeleton-text w-75 mb-2" />
                <div className="qs-skeleton-text w-100 mb-1" />
                <div className="qs-skeleton-text w-50 mb-2" />
                <div className="qs-skeleton-text w-40" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  /** Empty state. */
  if (!loading && articles.length === 0) {
    return (
      <div className="text-center py-5">
        <svg className="mb-3" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        <h4 className="qs-muted mb-2">No articles found</h4>
        <p className="qs-muted" style={{ opacity: 0.6 }}>
          Try a different category or adjust your search
        </p>
      </div>
    );
  }

  /** Article grid. */
  return (
    <div className="row g-4">
      {articles.map((article, index) => (
        <div className="col-12 col-sm-6 col-md-4" key={`${article.url}-${index}`}>
          <NewsItem
            title={article.title}
            description={article.description}
            imageUrl={article.image}
            newsUrl={article.url}
            author={article.author}
            date={article.date}
            source={article.source}
            article={article}
            isBookmarked={isBookmarked(article.url)}
            onToggleBookmark={onToggleBookmark}
          />
        </div>
      ))}
    </div>
  );
};

NewsList.propTypes = {
  articles: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  isBookmarked: PropTypes.func.isRequired,
  onToggleBookmark: PropTypes.func.isRequired,
};

NewsList.displayName = 'NewsList';

export default NewsList;
